import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, FileText, Users } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { LeafletMap } from "@/components/map/LeafletMap";
import { exportNotesReport } from "@/lib/notesReport";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PALETTE = ["#C85A32", "#2563EB", "#7C3AED", "#059669", "#D97706", "#DB2777", "#0891B2", "#65A30D"];

export function AdminNotes() {
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [flyTo, setFlyTo] = useState(null);
  const [userFilter, setUserFilter] = useState("all");

  const load = () => api.get("/admin/notes", { params: { q } }).then(({ data }) => setNotes(data)).catch((err) => toast.error(errorMessage(err)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = useMemo(() => [...new Set(notes.map((n) => n.user_email))], [notes]);
  const colorOf = (email) => PALETTE[Math.max(0, users.indexOf(email)) % PALETTE.length];
  const shown = useMemo(() => notes.filter((n) => userFilter === "all" || n.user_email === userFilter).map((n) => ({ ...n, color: colorOf(n.user_email) })), [notes, userFilter, users]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (n) => {
    if (!window.confirm(`Delete this note by ${n.user_email}?`)) return;
    try { await api.delete(`/map/notes/${n.id}`); setNotes((ns) => ns.filter((x) => x.id !== n.id)); toast.success("Note deleted"); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="admin-notes" className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 rounded-2xl overflow-hidden border h-[520px] relative">
        <LeafletMap notes={shown} onDeleteNote={remove} flyTo={flyTo} />
        <div className="absolute top-3 left-3 z-[1000] glass rounded-full px-4 h-9 flex items-center gap-2 text-xs font-mono shadow-lg" data-testid="admin-notes-count"><Users size={12} /> {shown.length} notes · {users.length} surveyors</div>
      </div>
      <div className="lg:col-span-2 rounded-2xl border bg-card overflow-hidden flex flex-col">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input data-testid="admin-notes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search note text" className="pl-9 h-10 rounded-full" /></div>
            <Button data-testid="admin-notes-report-button" variant="outline" size="sm" className="rounded-full h-10" disabled={!shown.length} onClick={() => exportNotesReport(shown, { title: userFilter === "all" ? "Team Field Notes Report" : `Field Notes — ${userFilter}`, author: "Superadmin" }).then(() => toast.success("Report downloaded")).catch((e) => toast.error(e.message))}><FileText size={14} className="mr-1" /> PDF</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={() => setUserFilter("all")} className={`text-xs px-2.5 py-1 rounded-full border ${userFilter === "all" ? "bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950" : "hover:bg-muted"}`}>All</button>
            {users.map((u) => <button type="button" key={u} data-testid={`admin-notes-user-${u}`} onClick={() => setUserFilter(u)} className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${userFilter === u ? "bg-slate-900 text-white" : "hover:bg-muted"}`}><span className="w-2 h-2 rounded-full" style={{ background: colorOf(u) }} />{u}</button>)}
          </div>
        </div>
        <ul className="divide-y overflow-auto max-h-[400px]">
          {shown.length === 0 && <li className="p-6 text-sm text-muted-foreground text-center" data-testid="admin-notes-empty">No field notes yet.</li>}
          {shown.map((n) => (
            <li key={n.id} data-testid={`admin-note-row-${n.id}`} className="p-4 text-sm hover:bg-muted/50 cursor-pointer" onClick={() => setFlyTo({ center: [n.lat, n.lng], zoom: 16, ts: Date.now() })}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><span className="w-2 h-2 rounded-full" style={{ background: n.color }} /><span className="font-medium text-foreground">{n.user_name || n.user_email}</span>· {formatDate(n.created_at)}{n.has_photo && " · 📷"}</div>
              <p className="mt-1 line-clamp-2">{n.text || <i className="text-muted-foreground">Photo only</i>}</p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1">{n.lat.toFixed(5)}, {n.lng.toFixed(5)}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
